import {
  useAppDispatch,
  useAppSelector,
  useLanguageFromUrl,
} from "../../app/hooks";
import { PageContainer } from "../../components/page_container/PageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import logo from "../../assets/LogoSmall.webp";
import { selectState } from "./HomeSlice";
import { AppBar, Box, Stack, Typography, Divider, TextField, InputAdornment, IconButton } from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";
import { useState, useMemo } from "react";
import { HomeCard } from "../../components/home_card/HomeCard";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "../../components/language_selector/LanguageSelector";
import { useLocation } from "react-router-dom";
import {
  getLanguageUrl,
  getLanguageFromUrl,
} from "../../utilities/languageUrls";
import BaseConverterImage from "../../assets/BaseConverter.webp";
import BmiCalculatorImage from "../../assets/BmiCalculator.webp";
import WeightUnitConverterImage from "../../assets/WeightUnitConverter.webp";
import DistanceUnitConverterImage from "../../assets/DistanceUnitConverter.webp";
import FaviconGeneratorImage from "../../assets/FaviconGenerator.webp";
import TemperatureConverterImage from "../../assets/TemperatureConverter.webp";
import CompoundInterestCalculatorImage from "../../assets/CompoundInterestCalculator.webp";
import CreditCardRepaymentCalculatorImage from "../../assets/CreditCardRepaymentCalculator.webp";
import AndroidIosImageResizerImage from "../../assets/AndroidIosImageResizer.webp";
import PdfMergerImage from "../../assets/PdfMerger.webp";
import PdfCompressionImage from "../../assets/PdfCompression.webp";
import ImageConverterImage from "../../assets/ImageConverter.webp";
import CompressionImage from "../../assets/Compression.webp";
import QrCodeGeneratorImage from "../../assets/QrCodeGenerator.webp";
import BarcodeGeneratorImage from "../../assets/BarcodeGenerator.webp";
import CsvJsonConverterImage from "../../assets/CsvJsonConverter.webp";
import ImageCropperImage from "../../assets/ImageCropper.webp";
import EmailSignatureGeneratorImage from "../../assets/EmailSignatureGenerator.webp";
import ImageToPdfConverterImage from "../../assets/ImageToPdfConverter.webp";
import InvoiceGeneratorImage from "../../assets/InvoiceGenerator.webp";
import CountdownTimerImage from "../../assets/CountdownTimer.webp";
import DateCalculatorImage from "../../assets/DateCalculator.webp";
import TimezoneConverterImage from "../../assets/TimezoneConverter.webp";
import PasswordGeneratorImage from "../../assets/PasswordGenerator.webp";
import LoremIpsumGeneratorImage from "../../assets/LoremIpsumGenerator.webp";
import CaseConverterImage from "../../assets/CaseConverter.webp";
import UuidGeneratorImage from "../../assets/UuidGenerator.webp";
import Base64EncoderImage from "../../assets/Base64Encoder.webp";
import JsonToCSharpConverterImage from "../../assets/JsonToCSharpConverter.webp";
import JwtDecoderImage from "../../assets/JwtDecoder.webp";
import styles from "./Home.module.css";

// Category data structure
interface ToolCategory {
  title: string;
  description: string;
  tools: Array<{
    languageResource: string;
    link: string;
    image: string;
    isNew?: boolean;
    isPopular?: boolean;
  }>;
}

export function Home() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();
  const location = useLocation();
  const currentLanguage = getLanguageFromUrl(location.pathname);
  const [searchQuery, setSearchQuery] = useState("");

  // Automatically detect and change language when URL changes
  useLanguageFromUrl();

  // Function to generate language-specific URLs for tools
  const getToolUrl = (toolKey: string) =>
    getLanguageUrl(currentLanguage, toolKey);

  // Generate tool categories with language-specific URLs
  const toolCategories = useMemo(() => [
    {
      title: "fileMediaTools",
      description: "fileMediaToolsDescription",
      tools: [
        {
          languageResource: "pdfMerger",
          link: getToolUrl("pdfMerger"),
          image: PdfMergerImage,
        },
        {
          languageResource: "pdfCompression",
          link: getToolUrl("pdfCompression"),
          image: PdfCompressionImage, // Using PDF merger image as placeholder
          isNew: true,
        },
        {
          languageResource: "imageConverter",
          link: getToolUrl("imageConverter"),
          image: ImageConverterImage,
        },
        {
          languageResource: "compression",
          link: getToolUrl("compression"),
          image: CompressionImage,
        },
        {
          languageResource: "androidIosResizer",
          link: getToolUrl("androidIosResizer"),
          image: AndroidIosImageResizerImage,
          isPopular: true,
        },
        {
          languageResource: "imageCropper",
          link: getToolUrl("imageCropper"),
          image: ImageCropperImage,
          isNew: true,
        },
        {
          languageResource: "imageToPdf",
          link: getToolUrl("imageToPdf"),
          image: ImageToPdfConverterImage,
          isNew: true,
        },
      ],
    },
    {
      title: "webDevelopmentTools",
      description: "webDevelopmentToolsDescription",
      tools: [
        {
          languageResource: "faviconGenerator",
          link: getToolUrl("faviconGenerator"),
          image: FaviconGeneratorImage,
        },
        {
          languageResource: "qrCodeGenerator",
          link: getToolUrl("qrCodeGenerator"),
          image: QrCodeGeneratorImage,
          isPopular: true,
        },
        {
          languageResource: "barcodeGenerator",
          link: getToolUrl("barcodeGenerator"),
          image: BarcodeGeneratorImage,
          isNew: true,
        },
      ],
    },
    {
      title: "documentGenerators",
      description: "documentGeneratorsDescription",
      tools: [
        {
          languageResource: "emailSignature",
          link: getToolUrl("emailSignature"),
          image: EmailSignatureGeneratorImage,
        },
        {
          languageResource: "invoiceGenerator",
          link: getToolUrl("invoiceGenerator"),
          image: InvoiceGeneratorImage,
          isNew: true,
        },
      ],
    },
    {
      title: "dateTimeTools",
      description: "dateTimeToolsDescription",
      tools: [
        {
          languageResource: "countdownTimer",
          link: getToolUrl("countdownTimer"),
          image: CountdownTimerImage,
          isNew: true,
        },
        {
          languageResource: "dateCalculator",
          link: getToolUrl("dateCalculator"),
          image: DateCalculatorImage,
          isNew: true,
        },
        {
          languageResource: "timezoneConverter",
          link: getToolUrl("timezoneConverter"),
          image: TimezoneConverterImage,
          isNew: true,
        },
      ],
    },
    {
      title: "dataTextTools",
      description: "dataTextToolsDescription",
      tools: [
        {
          languageResource: "csvJsonConverter",
          link: getToolUrl("csvJsonConverter"),
          image: CsvJsonConverterImage,
          isNew: true,
        },
        {
          languageResource: "passwordGenerator",
          link: getToolUrl("passwordGenerator"),
          image: PasswordGeneratorImage,
          isNew: true,
        },
        {
          languageResource: "loremIpsum",
          link: getToolUrl("loremIpsum"),
          image: LoremIpsumGeneratorImage,
        },
        {
          languageResource: "caseConverter",
          link: getToolUrl("caseConverter"),
          image: CaseConverterImage, // Using Lorem Ipsum image as placeholder
          isNew: true,
        },
        {
          languageResource: "uuidGenerator",
          link: getToolUrl("uuidGenerator"),
          image: UuidGeneratorImage,
          isNew: true
        },
        {
          languageResource: "base64Encoder",
          link: getToolUrl("base64Encoder"),
          image: Base64EncoderImage,
          isNew: true
        },
        {
          languageResource: "jwtDecoder",
          link: getToolUrl("jwtDecoder"),
          image: JwtDecoderImage,
          isNew: true
        },
        {
          languageResource: "jsonToCSharp",
          link: getToolUrl("jsonToCSharp"),
          image: JsonToCSharpConverterImage,
          isNew: true
        },
      ],
    },
    {
      title: "unitConverters",
      description: "unitConvertersDescription",
      tools: [
        {
          languageResource: "baseConverter",
          link: getToolUrl("baseConverter"),
          image: BaseConverterImage,
        },
        {
          languageResource: "distanceConverter",
          link: getToolUrl("distanceConverter"),
          image: DistanceUnitConverterImage,
        },
        {
          languageResource: "weightConverter",
          link: getToolUrl("weightConverter"),
          image: WeightUnitConverterImage,
        },
        {
          languageResource: "temperatureConverter",
          link: getToolUrl("temperatureConverter"),
          image: TemperatureConverterImage,
        },
      ],
    },
    {
      title: "calculators",
      description: "calculatorsDescription",
      tools: [
        {
          languageResource: "bmiCalculator",
          link: getToolUrl("bmiCalculator"),
          image: BmiCalculatorImage,
        },
        {
          languageResource: "interestCalculator",
          link: getToolUrl("interestCalculator"),
          image: CompoundInterestCalculatorImage,
        },
        {
          languageResource: "creditCardCalculator",
          link: getToolUrl("creditCardCalculator"),
          image: CreditCardRepaymentCalculatorImage,
        },
      ],
    },
  ], [currentLanguage, getToolUrl]);

  // Filter tools based on search query
  const filteredToolCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      return toolCategories;
    }

    const query = searchQuery.toLowerCase();
    return toolCategories.map(category => ({
      ...category,
      tools: category.tools.filter(tool => {
        const toolTitle = t(`home.tools.${tool.languageResource}.title`).toLowerCase();
        const toolDescription = t(`home.tools.${tool.languageResource}.description`).toLowerCase();
        return toolTitle.includes(query) || toolDescription.includes(query);
      })
    })).filter(category => category.tools.length > 0);
  }, [searchQuery, toolCategories, t]);

  return (
    <Stack>
      <AppBar sx={{ backgroundColor: "#282b3900" }} position="fixed">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          className={styles.appbar}
          sx={{ px: 2 }}
        >
          <Stack direction="row" alignItems="center">
            <Box sx={{ width: { xs: 120, sm: 130, md: 150, lg: 150 }, ml: -3 }}>
              <img
                src={logo}
                alt="Number Chomper logo"
                className={`applogo ${styles.homeLogo}`}
              />
            </Box>

            <Stack direction="column" justifyContent="start">
              <Box
                sx={{
                  width: { xs: 200, sm: 250, md: 300, lg: 300 },
                  ml: 0,
                }}
              >
                <Typography
                  variant="h1"
                  sx={{ fontSize: { xs: 17, sm: 23, md: 25, lg: 25 } }}
                >
                  NumberChomper.com
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ fontSize: { xs: 10, sm: 11, md: 12, lg: 12 } }}
                >
                  {t("home.subtitle")}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          <LanguageSelector />
        </Stack>
      </AppBar>
      <PageContainer
        pageSeoTitle="Number Chomper | Free Online Tools"
        pageDescription="Online, Free, and Easy-To-Use Converters, Generators and Calculators to Help with Everyday Tasks"
      >
        {/* Preload critical images for LCP - only on home page */}
        <Helmet>
          <link
            rel="preload"
            href="/static/media/PdfMerger.fa832079dfe03e20aa3e.webp"
            as="image"
            type="image/webp"
          />
          <link
            rel="preload"
            href="/static/media/ImageConverter.7304fa1.webp"
            as="image"
            type="image/webp"
          />
          <link
            rel="preload"
            href="/static/media/Compression.c254b9b.webp"
            as="image"
            type="image/webp"
          />
          <link
            rel="preload"
            href="/static/media/AndroidIosImageResizer.7ab732f.webp"
            as="image"
            type="image/webp"
          />
        </Helmet>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            mt: 8,
          }}
        >
          <Stack direction="column" justifyContent="center" alignItems="center">
            <Box sx={{ width: { xs: 300, sm: 600, md: 900, lg: 1200 } }}>
              {/* Search Bar */}
              <Box sx={{ 
                mb: 2, 
                pt: 2,
                width: '100%', 
                maxWidth: 600, 
                mx: 'auto',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <TextField
                  fullWidth
                  placeholder={t('home.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#b8e6f7' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setSearchQuery('')}
                          size="small"
                          sx={{ 
                            color: '#b8e6f7',
                            '&:hover': {
                              color: '#ffffff',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            }
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#b8e6f7',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#b8e6f7',
                      },
                      '& input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.6)',
                        opacity: 1,
                      },
                    },
                  }}
                />
              </Box>

              {/* Search Results Info */}
              {searchQuery.trim() && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#b8e6f7',
                      opacity: 0.8,
                    }}
                  >
                    {t('home.searchResults', { 
                      count: filteredToolCategories.reduce((total, category) => total + category.tools.length, 0),
                      query: searchQuery 
                    })}
                  </Typography>
                </Box>
              )}

              {filteredToolCategories.map((category, categoryIndex) => (
                <Box key={category.title} sx={{ mt: 6 }}>
                  {/* Category Header */}
                  <Box sx={{ mb: 3, textAlign: "center" }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { xs: 20, sm: 24, md: 28 },
                        fontWeight: 600,
                        color: "#FFE4B5",
                        mb: 1,
                      }}
                    >
                      {t(`home.categories.${category.title}`)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: { xs: 14, sm: 16 },
                        color: "#b8e6f7",
                        opacity: 0.8,
                        maxWidth: 600,
                        mx: "auto",
                      }}
                    >
                      {t(`home.categories.${category.description}`)}
                    </Typography>
                  </Box>

                  {/* Category Tools Grid */}
                  <Grid2
                    container
                    spacing={3}
                    columns={{ xs: 3, sm: 6, md: 9, lg: 12 }}
                  >
                    {category.tools.map((tool, toolIndex) => (
                      <Grid2 xs={3} key={tool.languageResource}>
                        <HomeCard
                          languageResource={tool.languageResource}
                          link={tool.link}
                          image={tool.image}
                          isNew={tool.isNew}
                          isPopular={tool.isPopular}
                          isHighPriority={categoryIndex === 0 && toolIndex < 4} // High priority for first 4 tools in first category
                        />
                      </Grid2>
                    ))}
                  </Grid2>

                  {/* Divider between categories (except for the last one) */}
                  {categoryIndex < toolCategories.length - 1 && (
                    <Divider
                      sx={{
                        mt: 4,
                        mb: 2,
                        borderColor: "rgba(255, 255, 255, 0.1)",
                        opacity: 0.3,
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </Stack>
        </Box>
      </PageContainer>
    </Stack>
  );
}
